import { apiClient } from './client';
import type {
    PatientTestAssignmentState,
    PatientTestConsolidationStatus,
    PatientTestOverview,
    PatientTestStatus,
} from '@/types/patient-overview';

type TestQuestionValidationStyle =
    | 'text'
    | 'email'
    | 'phone'
    | 'cpf'
    | 'date'
    | 'number'
    | 'select'
    | 'multiselect';

type TestQuestionValidationInputMode = 'text' | 'email' | 'tel' | 'numeric';

type TestQuestionValidationNormalizeRule = 'trim' | 'lowercase' | 'digits_only';

export interface TestQuestionValidation {
    style: TestQuestionValidationStyle;
    input_mode?: TestQuestionValidationInputMode;
    mask?: string;
    regex?: string;
    message?: string;
    normalize?: TestQuestionValidationNormalizeRule[];
}

export interface TestQuestion {
    field_name: string;
    required: boolean;
    field_type: string;
    options?: string[] | { label: string; value: string }[];
    field_label?: string;
    validation?: TestQuestionValidation;
}

export interface TestSection {
    id: string;
    title: string;
    description: string;
    questions: TestQuestion[];
}

interface TestInterpretationTableRow {
    percentile: string;
    interpretation: string;
}

interface TestScoringInterpretation {
    factors?: Record<string, string>;
    scoring_rules?: string;
    interpretation_table?: TestInterpretationTableRow[];
}

export interface TestDetails {
    context?: string;
    indications?: string;
    target_audience?: string;
    technical_description?: string;
    application_instructions?: string;
    attribution?: string;
    scoring_interpretation?: TestScoringInterpretation;
    category?: string;
    estimated_minutes?: number;
}

export interface TestSummary {
    id: string;
    name: string;
    test_id: string;
    description: string | null;
    category?: string | null;
    target_audience?: string | null;
    question_count?: number | null;
    estimated_minutes?: number | null;
}

export interface TestDefinition extends TestSummary {
    sections: TestSection[];
    details?: TestDetails | null;
}

export type Test = TestSummary;

export interface TestAssignment {
    id: string;
    psychologist_id: string;
    patient_id: string;
    test_id: string;
    status: string;
    unlock_token: string;
    created_at: string;
    unlock_token_expires_at: string;
}

export interface TestAnswerPayload {
    answers: Record<string, unknown>;
    status: string;
}

interface TestAnswerResponse {
    id: string;
    name: string;
    instructions?: string;
    metadata?: Record<string, string> | TestQuestion[];
    answers?: Record<string, unknown>;
    status?: string;
    sections?: TestSection[];
}

interface TestAssignmentTokenResponse {
    token: string;
}

interface TestPatientAssignmentStatus {
    patient_id: string;
    latest_assignment_id: string;
    latest_status: PatientTestStatus;
    latest_created_at: string;
    assignment_state: PatientTestAssignmentState;
    can_assign: boolean;
    attempt_count: number;
}

interface TestOutcomePayload {
    answers: Record<string, unknown> | null;
    outcomes: Record<string, unknown> | null;
}

interface TestAssignmentAnswersResponse extends TestOutcomePayload {
    assignment_id: string;
    test_definition_id: string;
    test_identifier: string;
    test_name: string;
    status: PatientTestStatus;
    consolidation_status?: PatientTestConsolidationStatus;
    consolidation_error_code?: string | null;
    consolidation_error_message?: string | null;
    consolidation_updated_at?: string | null;
}

interface RetryTestConsolidationResponse {
    ok: true;
}

const mapMetadataToSections = (
    metadata: TestAnswerResponse['metadata'],
    name: string,
): TestSection[] | undefined => {
    if (!Array.isArray(metadata) || metadata.length === 0) return undefined;

    const questions: TestQuestion[] = metadata.map((item) => ({
        field_name: item.field_name,
        field_type: item.field_type,
        options: item.options,
        field_label: item.field_label,
        required: item.required ?? false,
    }));

    return [
        {
            id: `test-${name}`,
            title: name,
            description: '',
            questions,
        },
    ];
};

export const testsApi = {
    list: async (): Promise<TestSummary[]> => {
        const response = await apiClient.get<TestSummary[]>('/tests');
        return response as unknown as TestSummary[];
    },

    getById: async (id: string): Promise<TestDefinition> => {
        const response = await apiClient.get<{ data: TestDefinition }>(`/tests/${id}`);
        return (response as unknown as { data: TestDefinition }).data;
    },

    send: async (patientId: string, testId: string): Promise<TestAssignment> => {
        const response = await apiClient.post(`/patients/${patientId}/tests`, { testId });
        return (response as unknown as { data: TestAssignment }).data;
    },

    listPatientAssignments: async (patientId: string): Promise<PatientTestOverview[]> => {
        const response = await apiClient.get<{ data: PatientTestOverview[] }>(
            `/patients/${patientId}/tests`,
        );
        return (response as unknown as { data: PatientTestOverview[] }).data;
    },

    listPatientAssignmentStatuses: async (
        testId: string,
    ): Promise<TestPatientAssignmentStatus[]> => {
        const response = await apiClient.get<{
            data: TestPatientAssignmentStatus[];
        }>(`/tests/${testId}/patient-assignment-statuses`);
        return (response as unknown as { data: TestPatientAssignmentStatus[] }).data;
    },

    getAssignmentToken: async (
        patientId: string,
        assignmentId: string,
    ): Promise<TestAssignmentTokenResponse> => {
        const response = await apiClient.get<{
            data: TestAssignmentTokenResponse;
        }>(`/patients/${patientId}/tests/${assignmentId}/token`);
        return (response as unknown as { data: TestAssignmentTokenResponse }).data;
    },

    getAssignmentAnswers: async (
        patientId: string,
        assignmentId: string,
    ): Promise<TestAssignmentAnswersResponse> => {
        const response = await apiClient.get<{
            data: TestAssignmentAnswersResponse;
        }>(`/patients/${patientId}/tests/assignments/${assignmentId}/answer`);
        return (response as unknown as { data: TestAssignmentAnswersResponse }).data;
    },

    retryConsolidation: async (
        patientId: string,
        assignmentId: string,
    ): Promise<RetryTestConsolidationResponse> => {
        const response = await apiClient.post<RetryTestConsolidationResponse>(
            `/patients/${patientId}/tests/${assignmentId}/consolidation/retry`,
        );
        return response as unknown as RetryTestConsolidationResponse;
    },

    getTestByToken: async (token: string): Promise<TestAnswerResponse> => {
        const response = await apiClient.get<{ data: TestAnswerResponse }>(
            `/patients/test/answer?token=${token}`,
            { withCredentials: true },
        );
        const data = (response as unknown as { data: TestAnswerResponse }).data;
        return {
            ...data,
            sections: data.sections || mapMetadataToSections(data.metadata, data.name),
        };
    },

    submitTestAnswer: async (token: string, data: TestAnswerPayload): Promise<void> => {
        await apiClient.post(`/patients/test/answer?token=${token}`, data, {
            withCredentials: true,
        });
    },
};
